import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Portal,
    Autocomplete,
    TextField,
    Popper,
    Chip,
    IconButton,
    Tooltip,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../CustomDates/CustomDatePicker/CustomDatePicker';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import modalStyles from '../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import {
    cardsBetween,
    filterCardsByPalCode,
    palCodeOrderMapOf,
    palCodeTypesOf,
    sortCardsByPalCode,
    sortPalCodeOptions,
} from '../../../Utils/accountCardSelection';

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

/**
 * Picks what an open-items report should cover and over which period: pal code first
 * (one, a range or a whole type), then the cards or bank accounts themselves. The period is the
 * modal's own, so a report can span a different range than the screen behind it.
 * The caller owns the fetching and the files; this only hands it a list and a period.
 */
const UnmatchedReportModal = ({
    open,
    onClose,
    title,
    itemsLabel,
    items = [],
    palCodeOptions = [],
    direction = 'rtl',
    defaultStartDate = null,
    defaultEndDate = null,
    onDownload,              // (items, format, { startDate, endDate }) => Promise
    progress = null,         // { done, total } while the caller is fetching
    busy = false,
}) => {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';
    const textAlign = isRtl ? 'right' : 'left';

    const [palCodeMode, setPalCodeMode] = useState('all');
    const [selectedPalCode, setSelectedPalCode] = useState(null);
    const [fromPalCode, setFromPalCode] = useState(null);
    const [toPalCode, setToPalCode] = useState(null);
    const [palCodeType, setPalCodeType] = useState(null);

    const [cardMode, setCardMode] = useState('all');
    const [fromCard, setFromCard] = useState(null);
    const [toCard, setToCard] = useState(null);
    const [pickedCards, setPickedCards] = useState([]);

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    // Reopening starts from the screen's period again rather than from whatever was last reported on.
    useEffect(() => {
        if (!open) return;
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
    }, [open, defaultStartDate, defaultEndDate]);

    const hasPalCodes = palCodeOptions.length > 0;

    const sortedPalCodes = useMemo(() => sortPalCodeOptions(palCodeOptions), [palCodeOptions]);
    const palCodeTypeOptions = useMemo(
        () => palCodeTypesOf(palCodeOptions).map((type) => ({ label: type, value: type })),
        [palCodeOptions]
    );

    const cardsInPalCode = useMemo(
        () => sortCardsByPalCode(
            filterCardsByPalCode({
                cards: items,
                sortedPalCodeOptions: sortedPalCodes,
                mode: hasPalCodes ? palCodeMode : 'all',
                selectedPalCodes: selectedPalCode ? [selectedPalCode] : [],
                fromPalCode,
                toPalCode,
                palCodeType,
            }),
            palCodeOrderMapOf(sortedPalCodes)
        ),
        [items, sortedPalCodes, hasPalCodes, palCodeMode, selectedPalCode, fromPalCode, toPalCode, palCodeType]
    );

    const selectedItems = useMemo(() => {
        if (cardMode === 'range') return cardsBetween(cardsInPalCode, fromCard, toCard);
        if (cardMode === 'multiple') {
            const picked = new Set(pickedCards.map((card) => card.cardId));
            return cardsInPalCode.filter((card) => picked.has(card.cardId));
        }
        return cardsInPalCode;
    }, [cardsInPalCode, cardMode, fromCard, toCard, pickedCards]);

    const cardOptions = useMemo(
        () => cardsInPalCode.map((card) => ({
            value: card.cardId,
            label: `${card.cardId} - ${card.name}`,
            card,
        })),
        [cardsInPalCode]
    );

    const resetCardPickers = () => {
        setFromCard(null);
        setToCard(null);
        setPickedCards([]);
    };

    const handlePalCodeMode = (mode) => {
        setPalCodeMode(mode);
        setSelectedPalCode(null);
        setFromPalCode(null);
        setToPalCode(null);
        setPalCodeType(null);
        // The card pickers point at cards the pal code filter no longer leaves.
        resetCardPickers();
    };

    const handleCardMode = (mode) => {
        setCardMode(mode);
        resetCardPickers();
    };

    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const combo = ({ options, value, onChange, placeholder, multiple = false }) => (
        <Autocomplete
            className={fieldStyles.combo}
            size="small"
            fullWidth
            multiple={multiple}
            disableCloseOnSelect={multiple}
            options={options}
            value={value}
            onChange={(_, next) => onChange(next)}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            noOptionsText={t('NoOptions')}
            renderTags={multiple
                ? (tagValue, getTagProps) => tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} {...tagProps} size="small" label={option.label} />;
                })
                : undefined}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    fullWidth
                    placeholder={placeholder}
                    className={fieldStyles.styleInput}
                    inputProps={{ ...params.inputProps, style: { textAlign } }}
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return <li key={option.value ?? key} {...optionProps}>{option.label}</li>;
            }}
            ListboxProps={{ direction }}
            PopperComponent={renderComboPopper}
        />
    );

    const modeChips = (value, onPick, options) => (
        <div className={modalStyles.tabs} style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            {options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    className={`${modalStyles.tab}${value === option.value ? ` ${modalStyles.tabActive}` : ''}`}
                    onClick={() => onPick(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );

    // Chips and buttons sit on the side the text starts from, so in Hebrew they stay next to their
    // label on the right instead of drifting to the far edge of the modal.
    const startAligned = { justifyContent: 'flex-start' };

    // Fields share the row instead of huddling at one edge: a from/to pair takes half each, and a
    // single field takes the whole width, so no column of empty space is left beside them.
    const fieldRow = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '12px 18px',
        alignItems: 'end',
        marginBottom: 18,
    };

    const field = (label, node, span = 1) => (
        <div className={modalStyles.formField} style={{ width: 'auto', gridColumn: `span ${span}` }}>
            <span className={modalStyles.fieldLabel} dir={direction}>{label}</span>
            {node}
        </div>
    );

    const optionForCard = (card) =>
        (card ? cardOptions.find((option) => option.value === card.cardId) || null : null);

    if (!open) return null;

    const total = progress?.total ?? 0;
    const done = progress?.done ?? 0;
    const counter = total > 0 ? `${done} / ${total}` : '';

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${isRtl ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={busy ? undefined : onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    {/* Wider than the stock modal so a from/to pair of card pickers fits on one row. */}
                    <div
                        className={modalStyles.modal}
                        style={{ width: 'min(760px, calc(100vw - 32px))', minHeight: '46vh' }}
                    >
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close')}>
                                    <span>
                                        <IconButton onClick={onClose} disabled={busy} className={modalStyles.closeIcon}>
                                            <CancelIcon className={modalStyles.closeIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div>{title || t('UnmatchedCardsReport')}</div>
                                <div />
                            </div>

                            <div className={modalStyles.scroll}>
                                <p className={modalStyles.sectionTitle}>{t('Period')}</p>
                                <div style={fieldRow} dir={direction}>
                                    {field(t('From'), (
                                        <CustomDatePicker
                                            date={startDate ? new Date(startDate) : null}
                                            onDateChange={(date) => setStartDate(date)}
                                            height="40px"
                                            label=""
                                        />
                                    ))}
                                    {field(t('To'), (
                                        <CustomDatePicker
                                            date={endDate ? new Date(endDate) : null}
                                            onDateChange={(date) => setEndDate(date)}
                                            height="40px"
                                            label=""
                                        />
                                    ))}
                                </div>

                                {hasPalCodes && (
                                    <>
                                        <div className={modalStyles.toolbar} style={startAligned}>
                                            <p className={modalStyles.sectionTitle}>{t('PalCode')}</p>
                                            {modeChips(palCodeMode, handlePalCodeMode, [
                                                { value: 'all', label: t('All') },
                                                { value: 'single', label: t('Single') },
                                                { value: 'range', label: t('Range') },
                                                { value: 'type', label: t('PalCodeType') },
                                            ])}
                                        </div>
                                        <div style={fieldRow}>
                                            {palCodeMode === 'single' && field(t('SelectPalCode'), combo({
                                                options: sortedPalCodes,
                                                value: selectedPalCode,
                                                onChange: setSelectedPalCode,
                                                placeholder: t('SelectPalCode'),
                                            }), 2)}
                                            {palCodeMode === 'range' && (
                                                <>
                                                    {field(t('From'), combo({
                                                        options: sortedPalCodes,
                                                        value: fromPalCode,
                                                        onChange: setFromPalCode,
                                                        placeholder: t('From'),
                                                    }))}
                                                    {field(t('To'), combo({
                                                        options: sortedPalCodes,
                                                        value: toPalCode,
                                                        onChange: setToPalCode,
                                                        placeholder: t('To'),
                                                    }))}
                                                </>
                                            )}
                                            {palCodeMode === 'type' && field(t('PalCodeType'), combo({
                                                options: palCodeTypeOptions,
                                                value: palCodeType,
                                                onChange: setPalCodeType,
                                                placeholder: t('PalCodeType'),
                                            }), 2)}
                                        </div>
                                    </>
                                )}

                                <div className={modalStyles.toolbar} style={startAligned}>
                                    <p className={modalStyles.sectionTitle}>{itemsLabel || t('AccountCards')}</p>
                                    {modeChips(cardMode, handleCardMode, [
                                        { value: 'all', label: t('All') },
                                        { value: 'range', label: t('Range') },
                                        { value: 'multiple', label: t('Multiple') },
                                    ])}
                                </div>
                                <div style={fieldRow}>
                                    {cardMode === 'range' && (
                                        <>
                                            {field(t('From'), combo({
                                                options: cardOptions,
                                                value: optionForCard(fromCard),
                                                onChange: (option) => setFromCard(option?.card || null),
                                                placeholder: t('From'),
                                            }))}
                                            {field(t('To'), combo({
                                                options: cardOptions,
                                                value: optionForCard(toCard),
                                                onChange: (option) => setToCard(option?.card || null),
                                                placeholder: t('To'),
                                            }))}
                                        </>
                                    )}
                                    {cardMode === 'multiple' && field(t('SelectCards'), combo({
                                        options: cardOptions,
                                        value: pickedCards.map(optionForCard).filter(Boolean),
                                        onChange: (options) => setPickedCards(options.map((option) => option.card)),
                                        placeholder: t('SelectCards'),
                                        multiple: true,
                                    }), 2)}
                                </div>

                                <p className={modalStyles.muted}>
                                    {`${t('SelectedCards')}: ${selectedItems.length}`}
                                </p>

                                {busy && (
                                    <div style={{ marginTop: 18, paddingBottom: 6 }}>
                                        <p className={modalStyles.muted} style={{ marginBottom: 6 }}>
                                            {/* A single call covers every card, so there is a count to show only
                                                when the caller fetches item by item. */}
                                            {counter || t('LoadingData')}
                                        </p>
                                        <LinearProgress
                                            variant={counter ? 'determinate' : 'indeterminate'}
                                            value={counter ? (done / total) * 100 : undefined}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={modalStyles.footer} style={startAligned}>
                                <button
                                    type="button"
                                    className={modalStyles.primaryBtn}
                                    disabled={busy || selectedItems.length === 0}
                                    onClick={() => onDownload?.(selectedItems, 'pdf', { startDate, endDate })}
                                >
                                    {busy
                                        ? <CircularProgress size={18} color="inherit" />
                                        : <PictureAsPdfIcon fontSize="small" />}
                                    {t('DownloadPDFFile')}
                                </button>
                                <button
                                    type="button"
                                    className={modalStyles.ghostBtn}
                                    disabled={busy || selectedItems.length === 0}
                                    onClick={() => onDownload?.(selectedItems, 'excel', { startDate, endDate })}
                                >
                                    <TableChartIcon fontSize="small" />
                                    {t('DownloadExcelFile')}
                                </button>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose} disabled={busy}>
                                    {t('cancelButton')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default UnmatchedReportModal;
